/**
 * Per-pull-request file visibility for the pr-v2 diff: the files a reviewer marked viewed,
 * and the files they collapsed or expanded against the default.
 *
 * Each mark is pinned to the version of the file it was made on: the file's blob SHA, or the
 * head commit of the comparison when GitHub sent no blob SHA. A mark applies only while the file
 * is at that version, so a push clears the marks on the files it changes and keeps the rest, as
 * GitHub, GitLab and Gerrit do. Marks also carry across comparisons of the same head, such as the
 * automatic "changes since your last review" one.
 *
 * The diff view owns writes (use-pr-v2-file-visibility-state.ts); the chat
 * sidebar reads viewed files to aim the "next file to review" follow-up pill.
 *
 * Everything lives in one localStorage key, most recently saved pull request first and capped at
 * MAX_STORED_CHARS. It replaced a record keyed by each comparison's SHAs, which lost every mark on
 * a push, and that record replaced three keys per comparison that were never deleted and grew
 * until the origin's storage quota ran out.
 */

import { z } from 'zod';

import { safeLocalStorage } from '@/lib/safe-storage';

// A new key rather than a new shape under the old one: desktop windows run an old bundle for
// days, and an old bundle reads a record it can't parse as empty and overwrites it on its next
// save.
const STORAGE_KEY = 'pr-v2-file-visibility-v2';

/** The per-comparison record this one replaced. Folded in and deleted once per page load. */
const COMPARISON_RECORD_KEY = 'pr-v2-file-visibility';

/** Prefixes of the three keys per comparison used before the per-comparison record. */
const LEGACY_KEY_PREFIXES = [
  'pr-v2-viewed-files-',
  'pr-v2-collapsed-files-',
  'pr-v2-expanded-files-',
];

/** About 5% of Chrome's per-origin quota: a hundred or so pull requests of a typical size. */
export const MAX_STORED_CHARS = 250_000;

/** Path → the file version the mark was made on. */
const fileVersionsSchema = z.record(z.string(), z.string());

const storedPullRequestSchema = z.object({
  owner: z.string(),
  repo: z.string(),
  number: z.string(),
  viewed: fileVersionsSchema,
  userCollapsed: fileVersionsSchema,
  userExpanded: fileVersionsSchema,
});

/** A comparison in the record this one replaced: keyed by both SHAs, with bare file paths. */
const storedComparisonSchema = z.object({
  owner: z.string(),
  repo: z.string(),
  number: z.string(),
  headSha: z.string().nullable(),
  viewed: z.array(z.string()),
  userCollapsed: z.array(z.string()),
  userExpanded: z.array(z.string()),
});

type StoredPullRequest = z.infer<typeof storedPullRequestSchema>;

type StoredComparison = z.infer<typeof storedComparisonSchema>;

type PrV2PullRequestRef = Pick<StoredPullRequest, 'owner' | 'repo' | 'number'>;

export type PrV2FileVisibility = Pick<
  StoredPullRequest,
  'viewed' | 'userCollapsed' | 'userExpanded'
>;

type FileVisibilityList = keyof PrV2FileVisibility;

const FILE_VISIBILITY_LISTS: FileVisibilityList[] = ['viewed', 'userCollapsed', 'userExpanded'];

const EMPTY_FILE_VISIBILITY: PrV2FileVisibility = {
  viewed: {},
  userCollapsed: {},
  userExpanded: {},
};

// Blob SHAs are hex, so a head version never collides with one.
const HEAD_VERSION_PREFIX = 'head:';

function getHeadVersion(headSha: string): string {
  return `${HEAD_VERSION_PREFIX}${headSha}`;
}

/** A file of the comparison on screen. */
export type PrV2VersionedFile = {
  filename: string;
  /** The file's blob SHA at the comparison's head. */
  sha?: string | null;
};

/** The version each file is at in the comparison on screen. */
export type PrV2ComparisonFileVersions = {
  /** Blob SHA by path, for the files GitHub sent one for. */
  shaByFilename: ReadonlyMap<string, string>;
  /**
   * The comparison's head as a version, or null before the comparison is known. Marks on files
   * without a blob SHA are pinned to it, and so are marks folded from the per-comparison record,
   * which name only the head they were made on. Nothing tells whether such a file changed, so its
   * marks clear on every push.
   */
  headVersion: string | null;
};

export function getPrV2ComparisonFileVersions(
  files: readonly PrV2VersionedFile[],
  headSha: string | null | undefined,
): PrV2ComparisonFileVersions {
  const shaByFilename = new Map<string, string>();
  for (const { filename, sha } of files) {
    if (sha) {
      shaByFilename.set(filename, sha);
    }
  }
  return { shaByFilename, headVersion: headSha ? getHeadVersion(headSha) : null };
}

/** The version a mark made now pins the file to; null when nothing identifies one. */
export function getPrV2FileVersion(
  versions: PrV2ComparisonFileVersions,
  filename: string,
): string | null {
  return versions.shaByFilename.get(filename) ?? versions.headVersion;
}

/** Whether a mark made on `markVersion` applies to the file as the comparison shows it. */
export function isPrV2FileVersionCurrent(
  versions: PrV2ComparisonFileVersions,
  filename: string,
  markVersion: string,
): boolean {
  return (
    markVersion === versions.headVersion || markVersion === versions.shaByFilename.get(filename)
  );
}

/**
 * Whether the file's contents differ from the ones a mark was made on. A head version names no
 * contents, so a mark pinned to one never counts as changed.
 */
export function hasPrV2FileChangedSince(
  versions: PrV2ComparisonFileVersions,
  filename: string,
  markVersion: string,
): boolean {
  const sha = versions.shaByFilename.get(filename);
  return sha !== undefined && markVersion !== sha && !markVersion.startsWith(HEAD_VERSION_PREFIX);
}

/**
 * The blob SHA for a mark pinned to the head on screen, which keeps the mark through later pushes
 * that leave the file unchanged; any other mark keeps its version.
 */
export function repinPrV2FileVersion(
  versions: PrV2ComparisonFileVersions,
  filename: string,
  markVersion: string,
): string {
  return markVersion === versions.headVersion
    ? (versions.shaByFilename.get(filename) ?? markVersion)
    : markVersion;
}

function isSamePullRequest(left: PrV2PullRequestRef, right: PrV2PullRequestRef): boolean {
  return left.owner === right.owner && left.repo === right.repo && left.number === right.number;
}

function isEmptyVisibility(visibility: PrV2FileVisibility): boolean {
  return FILE_VISIBILITY_LISTS.every((list) => Object.keys(visibility[list]).length === 0);
}

/** Parses and validates stored JSON; anything missing or unreadable is null. */
function parseJson<T>(raw: string | null, schema: z.ZodType<T>): T | null {
  if (raw === null) {
    return null;
  }
  try {
    const result = schema.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

function loadRecord(): StoredPullRequest[] {
  return parseJson(safeLocalStorage.getItem(STORAGE_KEY), z.array(storedPullRequestSchema)) ?? [];
}

/**
 * Keeps the most recent pull requests that fit in MAX_STORED_CHARS. The first one, the pull
 * request being saved, is always kept: a PR too big for the cap still keeps its own state and
 * just leaves no room for history.
 */
function fitWithinBudget(pullRequests: StoredPullRequest[]): StoredPullRequest[] {
  const kept: StoredPullRequest[] = [];
  let size = 2; // The enclosing brackets.
  for (const pullRequest of pullRequests) {
    const pullRequestSize = JSON.stringify(pullRequest).length + 1; // Plus its comma.
    if (kept.length > 0 && size + pullRequestSize > MAX_STORED_CHARS) {
      continue;
    }
    kept.push(pullRequest);
    size += pullRequestSize;
  }
  return kept;
}

/** Returns whether storage holds the record afterwards: a full quota fails the save silently. */
function saveStoredPullRequests(pullRequests: StoredPullRequest[]): boolean {
  const raw = pullRequests.length > 0 ? JSON.stringify(pullRequests) : null;
  // Every open diff saves again after another tab's save reaches it; an unchanged record would
  // only wake the other tabs to read it again.
  if (safeLocalStorage.getItem(STORAGE_KEY) === raw) {
    return true;
  }
  if (raw === null) {
    safeLocalStorage.removeItem(STORAGE_KEY);
  } else {
    safeLocalStorage.setItem(STORAGE_KEY, raw);
  }
  return safeLocalStorage.getItem(STORAGE_KEY) === raw;
}

/**
 * Turns comparisons, newest first, into per-PR marks pinned to each comparison's head. The newest
 * comparison that marks a file decides its version; a mark pinned to an older head no longer
 * applies to the PR's latest head.
 */
function pinComparisonsToHeads(comparisons: StoredComparison[]): StoredPullRequest[] {
  const byPullRequest = new Map<string, StoredPullRequest>();
  for (const comparison of comparisons) {
    // The diff renders, and so takes marks, only once both SHAs are known; a comparison without
    // a head holds none that any comparison could show again.
    if (!comparison.headSha) {
      continue;
    }
    const { owner, repo, number } = comparison;
    const key = JSON.stringify([owner, repo, number]);
    const pullRequest = byPullRequest.get(key) ?? {
      owner,
      repo,
      number,
      viewed: {},
      userCollapsed: {},
      userExpanded: {},
    };
    const version = getHeadVersion(comparison.headSha);
    for (const list of FILE_VISIBILITY_LISTS) {
      // This comparison is older than the ones already folded, so their versions win.
      pullRequest[list] = {
        ...Object.fromEntries(comparison[list].map((filename) => [filename, version])),
        ...pullRequest[list],
      };
    }
    byPullRequest.set(key, pullRequest);
  }
  return [...byPullRequest.values()].filter((pullRequest) => !isEmptyVisibility(pullRequest));
}

/** Adds folded marks for the files the record doesn't mark yet: its own marks are newer. */
function mergeFoldedPullRequests(
  record: StoredPullRequest[],
  folded: StoredPullRequest[],
): StoredPullRequest[] {
  const merged = record.map((entry) => {
    const foldedEntry = folded.find((candidate) => isSamePullRequest(candidate, entry));
    if (!foldedEntry) {
      return entry;
    }
    const mergedEntry = { ...entry };
    for (const list of FILE_VISIBILITY_LISTS) {
      mergedEntry[list] = { ...foldedEntry[list], ...entry[list] };
    }
    return mergedEntry;
  });
  const added = folded.filter(
    (foldedEntry) => !record.some((entry) => isSamePullRequest(entry, foldedEntry)),
  );
  return [...merged, ...added];
}

let hasFoldedOlderRecords = false;

/**
 * Once per page load, folds the per-comparison record into this one. Its marks name no file
 * versions, so each one is pinned to the head of its comparison: it keeps applying to that head,
 * and the diff re-pins it to the file's blob SHA when that head is on screen, so it also survives
 * the next push. Marks already in this record win over folded ones, which are older: a window
 * still running an old bundle can save the old record after this one exists, and the next page
 * load folds in the files it marked.
 *
 * The old record is deleted only once this one holds its marks, so a full quota defers the fold
 * to a later page load instead of losing them. The result is left untrimmed: the next save of a
 * pull request with marks moves it to the front and trims the rest.
 *
 * The keys before the per-comparison record are deleted without folding: nothing has written
 * them since 2026-09-25, and their marks would apply only to PRs nobody has pushed to since.
 */
function foldOlderRecordsOnce(): void {
  if (hasFoldedOlderRecords || typeof window === 'undefined') {
    return;
  }
  hasFoldedOlderRecords = true;

  deleteLegacyKeys();
  const rawComparisonRecord = safeLocalStorage.getItem(COMPARISON_RECORD_KEY);
  if (rawComparisonRecord === null) {
    return;
  }
  const folded = pinComparisonsToHeads(
    parseJson(rawComparisonRecord, z.array(storedComparisonSchema)) ?? [],
  );
  if (
    folded.length === 0 ||
    saveStoredPullRequests(mergeFoldedPullRequests(loadRecord(), folded))
  ) {
    safeLocalStorage.removeItem(COMPARISON_RECORD_KEY);
  }
}

function deleteLegacyKeys(): void {
  try {
    const legacyKeys = Array.from(
      { length: window.localStorage.length },
      (_, index) => window.localStorage.key(index) ?? '',
    ).filter((key) => LEGACY_KEY_PREFIXES.some((prefix) => key.startsWith(prefix)));
    legacyKeys.forEach((key) => safeLocalStorage.removeItem(key));
  } catch {
    // Storage blocked: nothing to delete.
  }
}

function readStoredPullRequests(): StoredPullRequest[] {
  foldOlderRecordsOnce();
  return loadRecord();
}

export function readPrV2FileVisibility(pullRequest: PrV2PullRequestRef): PrV2FileVisibility {
  const { viewed, userCollapsed, userExpanded } =
    readStoredPullRequests().find((entry) => isSamePullRequest(entry, pullRequest)) ??
    EMPTY_FILE_VISIBILITY;
  return { viewed, userCollapsed, userExpanded };
}

/** Saves the pull request as the most recent one. Empty maps remove it. */
export function writePrV2FileVisibility(
  pullRequest: PrV2PullRequestRef,
  visibility: PrV2FileVisibility,
): void {
  const stored = readStoredPullRequests();
  const others = stored.filter((entry) => !isSamePullRequest(entry, pullRequest));
  if (isEmptyVisibility(visibility)) {
    // The diff saves on every open, usually with nothing to save. Removing never grows
    // the record, so skip trimming: it would cut migrated history before any pull request
    // could claim the front.
    if (others.length < stored.length) {
      saveStoredPullRequests(others);
    }
    return;
  }
  const { owner, repo, number } = pullRequest;
  saveStoredPullRequests(
    fitWithinBudget([
      {
        owner,
        repo,
        number,
        viewed: visibility.viewed,
        userCollapsed: visibility.userCollapsed,
        userExpanded: visibility.userExpanded,
      },
      ...others,
    ]),
  );
}

/** Calls `onChange` when another tab saves file visibility. Returns the unsubscribe. */
export function subscribeToPrV2FileVisibilityChanges(onChange: () => void): () => void {
  const handleStorage = (event: StorageEvent) => {
    // A null key is another tab clearing all of storage.
    if (event.key === STORAGE_KEY || event.key === null) {
      onChange();
    }
  };
  window.addEventListener('storage', handleStorage);
  return () => {
    window.removeEventListener('storage', handleStorage);
  };
}

/** Files of one PR whose viewed mark applies to them as the comparison shows them, as in the diff. */
export function readPrV2ViewedFilesForPr(
  pullRequest: PrV2PullRequestRef,
  versions: PrV2ComparisonFileVersions,
): string[] {
  return Object.entries(readPrV2FileVisibility(pullRequest).viewed)
    .filter(([filename, version]) => isPrV2FileVersionCurrent(versions, filename, version))
    .map(([filename]) => filename);
}
